import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { ArrowUpRight, Check, Copy, Github, Linkedin, Mail, MessageSquare, Send, User } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { copyText } from '../utils/copyText';
import './Contact.css';

// EmailJS configuration (set these in .env locally and in the hosting dashboard)
const service = process.env.REACT_APP_SERVICE;
const template = process.env.REACT_APP_TEMPLATE;
const publickey = process.env.REACT_APP_PUBLIC_KEY;
const isConfigured = Boolean(service && template && publickey);

const EMAIL = 'phanicharan116@gmail.com';
const MAX_MESSAGE = 1500;

// X (formerly Twitter) has no lucide icon, so the logo is inlined.
const XIcon = ({ size = 20, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const channels = [
  { name: 'Email', handle: EMAIL, href: `mailto:${EMAIL}`, icon: Mail, copy: true },
  { name: 'LinkedIn', handle: '/in/phanicharan04', href: 'https://www.linkedin.com/in/phanicharan04', icon: Linkedin },
  { name: 'GitHub', handle: '@phanicharan04', href: 'https://github.com/phanicharan04', icon: Github },
  { name: 'X', handle: '@phanicharan04', href: 'https://x.com/phanicharan04', icon: XIcon }
];

const EMPTY_FORM = { from_name: '', from_email: '', message: '' };

const Contact = () => {
  const reduceMotion = useReducedMotion();
  const form = useRef(null);
  const honeypot = useRef(null);
  const copyTimer = useRef(null);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [emailCopied, setEmailCopied] = useState(false);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  // One entrance pattern for the whole page; disabled for reduced-motion users.
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.15 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
        };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (status === 'error') setStatus('idle');
  };

  const handleCopyEmail = async () => {
    if (await copyText(EMAIL)) {
      setEmailCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setEmailCopied(false), 1600);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot: real visitors never see or fill this field, so a value means a bot.
    // Pretend it worked, but send nothing.
    if (honeypot.current && honeypot.current.value) {
      setStatus('sent');
      setFormData(EMPTY_FORM);
      return;
    }

    // Enter can submit the form even while the button is disabled, so the check lives here too.
    if (!canSend) return;

    if (!isConfigured) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      await emailjs.sendForm(service, template, form.current, publickey);
      setStatus('sent');
      setFormData(EMPTY_FORM);
    } catch (error) {
      // EmailJS rejects with { status, text } (e.g. 412 + the reason). Visitors only see the friendly
      // message below; the real reason goes to the console so a failing setup can be diagnosed.
      console.error('EmailJS send failed:', error && error.status, error && (error.text || error.message || error));
      setStatus('error');
    }
  };

  const sending = status === 'sending';

  // A row's numeral fills in once its field holds a usable value.
  const filled = {
    from_name: formData.from_name.trim().length > 0,
    from_email: /^\S+@\S+\.\S+$/.test(formData.from_email.trim()),
    message: formData.message.trim().length > 0
  };

  // The button only works once all three fields hold something usable.
  const canSend = filled.from_name && filled.from_email && filled.message;
  const started = Object.values(formData).some((value) => value.trim().length > 0);

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <SectionHeader
          eyebrow="Get in touch"
          lead="Let's"
          accent="Connect"
          subtitle="Have an idea, a collaboration, or a job opportunity? Or just want to connect? My inbox is always open!"
          {...rise()}
        />

        <div className="ct-layout">
          {/* ---------- Direct channels ---------- */}
          <motion.div className="ct-direct" {...rise(0.05)}>
            <p className="ct-label">Reach me directly</p>

            <ul className="ct-channels">
              {channels.map(({ name, handle, href, icon: Icon, copy }) => (
                <li key={name} className="ct-channel-item">
                  <a
                    href={href}
                    className="ct-channel"
                    {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    <span className="ct-icon">
                      <Icon size={20} />
                    </span>
                    <span className="ct-text">
                      <span className="ct-name">{name}</span>
                      <span className="ct-handle">{handle}</span>
                    </span>
                    <ArrowUpRight className="ct-arrow" size={20} aria-hidden="true" />
                  </a>

                  {copy && (
                    <button
                      type="button"
                      className="ct-copy"
                      onClick={handleCopyEmail}
                      aria-label={emailCopied ? 'Email address copied' : 'Copy email address'}
                    >
                      {emailCopied ? <Check size={16} /> : <Copy size={16} />}
                      <span>{emailCopied ? 'Copied' : 'Copy'}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ---------- Message form ---------- */}
          <motion.div className="ct-card" {...rise(0.1)}>
            {status === 'sent' ? (
              <div className="ct-success" role="status">
                <span className="ct-success-icon">
                  <Check size={28} />
                </span>
                <h3>Message sent</h3>
                <p>Thanks for reaching out. I&apos;ll get back to you soon.</p>
                <button type="button" className="ct-ghost" onClick={() => setStatus('idle')}>
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className="ct-card-head">
                  <h3>Send me a message</h3>
                </div>

                <form ref={form} onSubmit={handleSubmit} className="ct-form">
                  <div className={`ct-row ${filled.from_name ? 'is-filled' : ''}`}>
                    <span className="ct-mark" aria-hidden="true"><User size={18} /></span>
                    <div className="ct-field">
                      <label htmlFor="from_name">Your name</label>
                      <input
                        type="text"
                        id="from_name"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        placeholder="Full name"
                      />
                    </div>
                  </div>

                  <div className={`ct-row ${filled.from_email ? 'is-filled' : ''}`}>
                    <span className="ct-mark" aria-hidden="true"><Mail size={18} /></span>
                    <div className="ct-field">
                      <label htmlFor="from_email">Where I can reply</label>
                      <input
                        type="email"
                        id="from_email"
                        name="from_email"
                        value={formData.from_email}
                        onChange={handleChange}
                        required
                        autoComplete="email"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>

                  <div className={`ct-row ${filled.message ? 'is-filled' : ''}`}>
                    <span className="ct-mark" aria-hidden="true"><MessageSquare size={18} /></span>
                    <div className="ct-field">
                      <label htmlFor="message">What&apos;s on your mind</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        maxLength={MAX_MESSAGE}
                        placeholder="Tell me about the idea, role, or project..."
                      />
                      <span className="ct-count" aria-hidden="true">
                        {formData.message.length}/{MAX_MESSAGE}
                      </span>
                    </div>
                  </div>

                  {/* Honeypot: hidden from people and assistive tech, tempting to bots */}
                  <div className="ct-hp" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input type="text" id="website" name="website" ref={honeypot} tabIndex={-1} autoComplete="off" />
                  </div>

                  <div className="ct-actions">
                    <button type="submit" className={`ct-submit ${sending ? 'is-sending' : ''}`} disabled={sending || !canSend}>
                      {sending ? (
                        <>
                          <span className="ct-spinner" aria-hidden="true" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send message
                        </>
                      )}
                    </button>

                    <p className={`ct-status ${status === 'error' ? 'is-error' : ''}`} role="status" aria-live="polite">
                      {status !== 'error' && started && !canSend && 'Fill in all three fields to send.'}
                      {status === 'error' && (
                        <>
                          {isConfigured
                            ? "Couldn't send your message. Please try again, or email me directly at "
                            : 'The form is not available right now. Please email me directly at '}
                          <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                        </>
                      )}
                    </p>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
